// Custom hook for search functionality
import { useState, useCallback } from 'react';
import { useFileStore } from '@stores/useFileStore';

export interface UseSearchOptions {
  onSearchComplete?: (query: string) => void;
  onClear?: () => void;
  autoSearch?: boolean; // If true, automatically calls searchFiles, otherwise just returns filtered results
}

export interface UseSearchReturn {
  searchQuery: string;
  isSearching: boolean;
  handleSearch: (query: string) => Promise<void>;
  handleClear: () => Promise<void>;
  setSearchQuery: (query: string) => void;
}

export const useSearch = (options: UseSearchOptions = {}): UseSearchReturn => {
  const { onSearchComplete, onClear, autoSearch = true } = options;
  const [localQuery, setLocalQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  // Use selective selectors to prevent re-renders when files change
  const searchQuery = useFileStore((state) => state.searchQuery);
  const searchFiles = useFileStore((state) => state.searchFiles);
  const fetchFiles = useFileStore((state) => state.fetchFiles);
  
  // Use store query if available, otherwise use local query
  const finalQuery = searchQuery || localQuery;

  const handleSearch = useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    setLocalQuery(query);
    
    if (trimmedQuery === '') {
      setIsSearching(false);
      if (autoSearch) {
        await fetchFiles();
      }
      onClear?.();
      return;
    }
    
    setIsSearching(true);
    
    if (autoSearch) {
      try {
        await searchFiles(trimmedQuery);
        setIsSearching(false);
        onSearchComplete?.(trimmedQuery);
      } catch (error) {
        console.error('Search error:', error);
        setIsSearching(false);
        // Don't show error toast - let the UI show "no results" message instead
        // Just clear results on error
        await fetchFiles();
      }
    } else {
      // Just update state, don't call searchFiles
      setIsSearching(false);
      onSearchComplete?.(trimmedQuery);
    }
  }, [autoSearch, searchFiles, fetchFiles, onSearchComplete, onClear]);

  const handleClear = useCallback(async () => {
    setLocalQuery('');
    setIsSearching(false);
    if (autoSearch) {
      await fetchFiles();
    }
    onClear?.();
  }, [autoSearch, fetchFiles, onClear]);
  
  const setSearchQuery = useCallback((query: string) => {
    setLocalQuery(query);
    if (autoSearch && query.trim()) {
      handleSearch(query);
    }
  }, [autoSearch, handleSearch]);

  return {
    searchQuery: finalQuery,
    isSearching,
    handleSearch,
    handleClear,
    setSearchQuery,
  };
};

